import { FC } from 'react';
import { Typography, Grid, List, ListItem, ListItemText } from '@mui/material';
import AlertDialog from 'components/AlertDialog';
import GridContainer from 'components/GridContainer';
import Heading from 'components/Heading';
import Spacing from 'components/Spacing';

type ImportErrors = {
  transactionTypeIdsInUse?: Array<string>;
  accountCodeNameConflicts?: Array<{
    accountCode: string;
    accountNames: Array<string>;
  }>;
  accountNameCodeConflicts?: Array<{
    accountName: string;
    accountCodes: Array<string>;
  }>;
};

type Props = {
  open: boolean;
  onClose: () => void;
  errors: ImportErrors | null;
};

const ImportErrorsDialog: FC<Props> = ({ open, onClose, errors }) => {
  if (!errors) return null;

  const {
    transactionTypeIdsInUse,
    accountCodeNameConflicts,
    accountNameCodeConflicts,
  } = errors;

  return (
    <AlertDialog
      open={open}
      onClose={onClose}
      title="Import Errors"
      btnLabels={{
        cancel: 'Close',
        confirm: false,
      }}
      cancelBtnProps={{
        variant: 'outlined',
        color: 'primary',
      }}
      centeredContent={false}
      size="md"
      content={
        <GridContainer>
          <Spacing size="sm">
            {transactionTypeIdsInUse && transactionTypeIdsInUse.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Heading variant="h2">Transaction Types In Use</Heading>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  The following transaction types cannot be deleted because they
                  have transactions:
                </Typography>
                <List>
                  {transactionTypeIdsInUse.map((errorString) => {
                    const parts = errorString.split(':');
                    const id = parts[0];
                    const name = parts[1] || 'Unknown';
                    const accountCode = parts[2] || 'Unknown';

                    return (
                      <ListItem key={errorString}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              <strong>{name}</strong> (Account: {accountCode})
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {id}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Grid>
            )}

            {accountCodeNameConflicts &&
              accountCodeNameConflicts.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Heading variant="h2">Account Code Conflicts</Heading>
                  <Typography variant="body2" color="text.secondary">
                    The same account code has been used with different account
                    names:
                  </Typography>
                  <List>
                    {accountCodeNameConflicts.map((conflict) => (
                      <ListItem key={conflict.accountCode}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              Account code:{' '}
                              <strong>{conflict.accountCode}</strong>
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2">
                              Account names:{' '}
                              <strong>
                                {conflict.accountNames.join(', ')}
                              </strong>
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}

            {accountNameCodeConflicts &&
              accountNameCodeConflicts.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Heading variant="h2">Account Name Conflicts</Heading>
                  <Typography variant="body2" color="text.secondary">
                    The same account name has been used with different account
                    codes:
                  </Typography>
                  <List>
                    {accountNameCodeConflicts.map((conflict) => (
                      <ListItem key={conflict.accountName}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              Account name:{' '}
                              <strong>{conflict.accountName}</strong>
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2">
                              Account codes:{' '}
                              <strong>
                                {conflict.accountCodes.join(', ')}
                              </strong>
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}
          </Spacing>
        </GridContainer>
      }
    />
  );
};

export default ImportErrorsDialog;
